import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { TextField, Autocomplete, IconButton } from '@mui/material';
import ClearIcon from 'src/icons/untitled-ui/free/x-clear';
import CheckIcon from '@mui/icons-material/Check';

const buildSelectedPayload = (option) => {
  if (!option) return null;

  const { value, label, ...rest } = option;
  return { value, label, ...rest }; // full object with dynamic fields
};

const SearchSelectField = forwardRef(({
  label,
  name,
  options = [],
  defaultValue,
  onSelected = () => {},
  hintValue,
  formik,
  disabled = false,
  onlyRow = false,
  showClearIcon = false,
}, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [currentOptions, setCurrentOptions] = useState(options);
  const inputRef = useRef(null); // Ref for the input field

  useEffect(() => {
    if (Array.isArray(options)) {
      setCurrentOptions(options);
    }
    if (defaultValue) {
      const matchedOption = options.find(
        (option) => option.value === defaultValue,
      );
      if (matchedOption) {
        setSelectedValue(matchedOption);
        setSearchTerm(matchedOption.label || '');
        onSelected(matchedOption); // Trigger the onSelected as if user selected
        if (formik) {
          formik.setFieldValue(name, matchedOption);
          formik.setFieldError(name, undefined); // Clear any existing error
        }
      } else {
        setSelectedValue(null);
        setSearchTerm('');
      }
    } else {
      setSelectedValue(null);
      setSearchTerm('');
    }
  }, [options, defaultValue]); // Listen to changes in options or defaultValue

  const handleSearch = (event, newInputValue) => {
    setSearchTerm(newInputValue);

    // Clear the error message in Formik if input is provided
    if (formik && newInputValue) {
      formik.setFieldError(name, undefined); // Clear the error
    }
  };

  const formatValue = (val) => {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val || '';
  };

  const resetSelection = (notify = false) => {
    setSelectedValue(null);
    setSearchTerm('');

    if (formik && name) {
      formik.setFieldValue(name, null);
      formik.setFieldTouched(name, false);
      formik.setFieldError(name, undefined);
    }

    // Inform (Notify) parent that user cleared the field (if needed)
    if (notify && typeof onSelected === 'function') {
      onSelected(null);
    }

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.blur(); // Close keyboard
    }
  };

  useImperativeHandle(ref, () => ({
    reset: () => resetSelection(),
    resetAndNotify: () => resetSelection(true)
  }));

  const handleReset = () => {
    resetSelection(true); // Notify on manual reset
  };

  return (
    onlyRow ? (
      <TextField
        fullWidth
        label={label}
        variant="outlined"
        value={formatValue(hintValue)}
        InputProps={{
          readOnly: true,
        }}
        tabIndex={-1}
        style={{
          pointerEvents: 'none',
        }}
      />
    ) : (
      <Autocomplete
        value={selectedValue} // Use selectedValue to bind to the selected item
        options={currentOptions} // Dynamically updated options
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        noOptionsText="Không tìm thấy"
        disabled={disabled}
        onInputChange={handleSearch} // Set search term for filtering 
        // renderOption={(props, option, { selected }) => (
        //   <li {...props} key={option.value} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        //     <span>{option.label}</span>
        //     {/* You can conditionally show icon based on value or selection */}
        //     <CheckIcon fontSize="small" color={selected ? "primary" : "disabled"} />
        //   </li>
        // )}
        onChange={(event, newValue) => {
          setSelectedValue(newValue);
          const selectedPayload  = buildSelectedPayload(newValue);
          onSelected(selectedPayload ); // Propagate the selected value
          if (formik) {
            formik.setFieldValue(name, selectedPayload); // Update Formik field value
            if (newValue) {
              formik.setFieldError(name, undefined); // Clear the error
            }
          }
          
          // Hide keyboard by blurring the input field
          if (inputRef.current) {
            inputRef.current.blur();
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef} // Assign the ref to the TextField
            label={label}
            value={searchTerm} // Bind input to search term
            variant="outlined"
            error={formik && !!(formik.touched[name] && formik.errors[name])} // Error handling
            helperText={formik && formik.touched[name] && formik.errors[name]} // Display errors
            placeholder={hintValue ? String(hintValue) : ''} // Display the hint value if present
            InputLabelProps={{
              shrink: hintValue ? true : undefined, // Force shrink if hintValue is present
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {showClearIcon && hintValue && !selectedValue && (
                    <IconButton
                      size="small"
                      sx={{ m: 0, p: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReset();
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    )
  );
});

SearchSelectField.propTypes = {
  formik: PropTypes.object, // Optional
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  defaultValue: PropTypes.any, // Expected to be a primitive value (e.g., ID)
  onSelected: PropTypes.func,
  hintValue: PropTypes.string,
  disabled: PropTypes.bool,
  showClearIcon: PropTypes.bool,
};

export default SearchSelectField;
